import React, { useState } from 'react';
import { MenuItem, ListItemIcon } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { deleteEmissionsAllocationDimension, EmissionsAllocationDetails } from 'api';
import { DeleteDialog } from 'components';
import { useNotify } from 'hooks';

interface DeleteColumnButtonProps {
    dimension: string | null;
    allocationId: number;
}

export const DeleteColumnButton = ({ dimension, allocationId }: DeleteColumnButtonProps) => {
    const notify = useNotify();
    const queryClient = useQueryClient();
    const [isDeleteColumnModalOpen, setIsDeleteColumnModalOpen] = useState(false);

    const { mutateAsync } = useMutation<
        EmissionsAllocationDetails,
        AxiosError,
        { allocationId: number; dimension: string }
    >(
        ({ allocationId: allocId, dimension: dim }) => deleteEmissionsAllocationDimension(allocId, dim),
        {
            onSuccess: () => {
                notify.success('Column successfully deleted.');
                queryClient.invalidateQueries(['emissionsAllocationDetails', allocationId]);
                toggleDeleteColumnModal();
            },
            onError: (error: AxiosError) => {
                notify.error(typeof error.response?.data === 'string' ? error.response?.data : 'Failed to delete column');
                toggleDeleteColumnModal();
            },
        }
    );

    const handleDelete = async () => {
        if (dimension) {
            await mutateAsync({ allocationId, dimension });
        }
    };

    const toggleDeleteColumnModal = () => setIsDeleteColumnModalOpen(!isDeleteColumnModalOpen);

    return <><MenuItem
        key="delete"
        onClick={toggleDeleteColumnModal}
        sx={{ color: 'error.main' }}
    >
        <ListItemIcon>
            <DeleteIcon color="error" />
        </ListItemIcon>
        {`Delete ${dimension} column`}
    </MenuItem>
        {isDeleteColumnModalOpen && <DeleteDialog
            isOpen={isDeleteColumnModalOpen}
            onCancel={toggleDeleteColumnModal}
            onDeleteClick={handleDelete}
            target="EmissionsAllocationDimensionColumn"
            title={`Delete ${dimension} column?`}
        />}
    </>
};
